/* eslint-disable camelcase */
import { trackIt } from '@vfde-sails/tracking';
import {
    NAME_ACCORDION,
    NAME_FOOTNOTE,
    TRACKING_ADDITIONAL_PAGE_LEVELS,
    TRACKING_INFORMATION,
    TRACKING_INFORMATION_ACTION_REVEAL,
    TRACKING_PAGE_VIEW, TRACKING_STEP_MAP,
    TRACKING_TRIGGER_REFERENCE_FOOTNOTE,
} from './constant';
import { SalesLeadErrorResponse, TrackingInformation, TrackingPageviewError } from './interface';
import { formatToTrackingString } from '../../helpers/formatter.helper';
import { debugInfo } from '../../utils/debug';
import { DXLErrorCode, FormStatus, TrackingErrorType } from './enum';
import { ApiErrorResponseWithCode } from '@vfde-sails/utils';
import { isApiError } from '../../helpers/objectValidator.helper';

const getPageLevels = (stepName: string, siteStructure: string[] = []): string[] => {
    // copy without reference
    const vfSiteStructure: string[] = [...siteStructure];

    // remove all added pageLevels
    for (const pageLevel of TRACKING_ADDITIONAL_PAGE_LEVELS) {
        const index: number = vfSiteStructure.indexOf(pageLevel);

        (0 <= index) && vfSiteStructure.splice(index, 1);
    }

    vfSiteStructure.push(stepName);
    return vfSiteStructure;
};

const getErrorTrackingType = (dxlError?: DXLErrorCode): TrackingErrorType => {
    switch (dxlError) {
        case DXLErrorCode.BAD_REQUEST:
        case undefined:
            return TrackingErrorType.TECHNICAL;
        default:
            return TrackingErrorType.VALIDATION;
    }
};

const getErrorObject = (error: SalesLeadErrorResponse | ApiErrorResponseWithCode): TrackingPageviewError =>
    isApiError(error) ? {
        error_action: 'displayed',
        error_code: error.status.toString(),
        error_type: TrackingErrorType.TECHNICAL,
        error_message: error.message,
    } : {
        error_action: 'displayed',
        error_code: error.status,
        error_type: getErrorTrackingType(error.code),
        error_message: error.reason,
    };

/** Track the page view based on form status */
export const trackPageView = (
    formStatus: FormStatus,
    error?: SalesLeadErrorResponse | ApiErrorResponseWithCode
) => {
    try {
        const { loginStatus, siteStructure, pageType } = window.VF || {};
        const trackingStep = TRACKING_STEP_MAP[formStatus];
        const payload = {
            loginStatus: loginStatus ? loginStatus : 'not logged in',
            siteStructure: getPageLevels(trackingStep.formStepName, siteStructure),
            pageType: pageType || 'content',
            siteArea: 'service',
            platformType: 'responsive',
            oForm: {
                name: 'lead management futurize',
                type: 'application funnel',
                ...trackingStep,
            },
            ...error && getErrorObject(error),
        };

        trackIt(TRACKING_PAGE_VIEW, payload);
    } catch (e) {
        debugInfo('Track pageView failed: ', e);
    }
};

/** Track validation error */
export const trackValidationError = (invalidInput: string) => {
    try {
        const payload = {
            location: `${invalidInput} field`,
            message: window.brixPageOptions?.formErrors?.required,
            type: 'validation',
        };

        trackIt('error', payload);
    } catch (e) {
        debugInfo('trackValidationError failed: ', e);
    }
};

export const trackFootnoteLink = (text: string) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: formatToTrackingString(text),
            information_ui_type: NAME_FOOTNOTE,
            information_trigger: TRACKING_TRIGGER_REFERENCE_FOOTNOTE,
        };

        debugInfo('Track footnote link : ', payload);
        trackIt(TRACKING_INFORMATION, payload);
    } catch (e) {
        debugInfo('trackFootnoteLink failed: ', e);
    }
};

export const trackFootnoteAccordion = (text: string) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: formatToTrackingString(text),
            information_ui_type: NAME_FOOTNOTE,
            information_trigger: NAME_ACCORDION,
        };

        debugInfo('Track footnote accordion: ', payload);
        trackIt(TRACKING_INFORMATION, payload);
    } catch (e) {
        debugInfo('trackFootnoteAccordion failed: ', e);
    }
};
